// Copyright 2025, University of Maryland, All Rights Reserved
import React from "react"
import { BaseField, type BaseFieldEditorProps } from "./BaseField.tsx"
import { Label } from "@/components/ui/label.tsx"
import { Input } from "@/components/ui/input.tsx"

export const IntegerFieldEditor = React.memo(({
  field,
  onFieldUpdate,
  ...restProps
}: BaseFieldEditorProps) => {
  const config = field.config
  const min_value = config.min ?? 0
  const max_value = config.max ?? 1000

  const update_config = (config_updates: Record<string, any>) => {
    onFieldUpdate(field, {
      config: { ...config, ...config_updates }
    })
  }

  const handle_min_change = (value: number) => {
    update_config({ min: value })
  }

  const handle_max_change = (value: number) => {
    update_config({ max: value })
  }

  return (
    <BaseField field={field} onFieldUpdate={onFieldUpdate} {...restProps}>
      <div className="space-y-3">
        <Label className="text-sm font-medium">Value Range</Label>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Min Value</Label>
            <Input
              type="number"
              value={min_value}
              onChange={(e) => {
                const num_value = e.target.value === '' ? 0 : parseInt(e.target.value, 10)
                if (!isNaN(num_value)) {
                  handle_min_change(num_value)
                }
              }}
              placeholder="0"
              className="text-sm h-8"
              disabled={restProps.isLoading}
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Max Value</Label>
            <Input
              type="number"
              value={max_value}
              onChange={(e) => {
                const num_value = e.target.value === '' ? 1000 : parseInt(e.target.value, 10)
                if (!isNaN(num_value)) {
                  handle_max_change(num_value)
                }
              }}
              placeholder="1000"
              className="text-sm h-8"
              disabled={restProps.isLoading}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Define the minimum and maximum values for this integer field
        </p>
      </div>
    </BaseField>
  )
})

IntegerFieldEditor.displayName = 'IntegerFieldEditor'
